import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { z } from "zod";
import { CatalogSlug, errors, paxRoute, Sha256, staffAuth } from "../../common";

export const AgentRole = z.enum(["gameMaster", "player", "grader"]);
export type AgentRole = z.infer<typeof AgentRole>;

export const AgentVersionStatus = z.enum([
  "draft",
  "publishing",
  "published",
  "refused",
  "in_appeal",
  "removed",
]);
export type AgentVersionStatus = z.infer<typeof AgentVersionStatus>;

/** The files a version names by role: `agent.json`, `bundle.tar`, and `source.tar`. */
export const AgentFileRole = z.enum(["agent", "bundle", "source"]);
export type AgentFileRole = z.infer<typeof AgentFileRole>;

export const AgentDetails = z.object({
  id: z.uuid(),
  slug: z.string(),
  /** The live version's name, or its newest draft's before the first publish. */
  name: z.string(),
  description: z.string(),
  role: AgentRole,
  ownerId: z.uuid(),
  liveVersionId: z.uuid().nullable(),
  archivedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type AgentDetails = z.infer<typeof AgentDetails>;

export const AgentVersionDetails = z.object({
  id: z.uuid(),
  status: AgentVersionStatus,
  version: z.number().int().nullable(),
  label: z.string().nullable(),
  createdFromVersionId: z.uuid().nullable(),
  name: z.string(),
  description: z.string(),
  about: z.string(),
  models: z.array(z.string()),
  /**
   * The version's own files, each with its role and SHA-256 so a push sends only what changed,
   * then every file inside its bundle.
   */
  files: z.array(
    z.object({
      path: z.string(),
      size: z.number(),
      contentType: z.string(),
      role: AgentFileRole.optional(),
      sha256: Sha256.optional(),
    }),
  ),
  problems: z.array(z.string()).nullable(),
  publishedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type AgentVersionDetails = z.infer<typeof AgentVersionDetails>;

export const AgentDetail = z.object({
  agent: AgentDetails,
  /** Published versions, newest first, then drafts to the agent's editors. */
  versions: z.array(AgentVersionDetails),
  authors: z.array(z.object({ id: z.uuid(), name: z.string(), username: z.string().nullable() })),
  canEdit: z.boolean(),
});
export type AgentDetail = z.infer<typeof AgentDetail>;

function endpoint(
  name: string,
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
  path: `/${string}`,
  summary: string,
) {
  return oc
    .meta(
      paxRoute({
        auth: staffAuth("agents.manage"),
        minimumTier: method === "GET" ? null : "suspicious",
        cost: method === "GET" ? "read" : "write",
      }),
      openapi({
        method,
        path,
        operationId: `agents.${name}`,
        summary,
        inputStructure: "detailed",
      }),
    )
    .errors({
      ...errors.unauthorized,
      ...errors.forbidden,
      ...errors.notFound,
      ...errors.badRequest,
      ...errors.conflict,
    });
}
const params = z.object({ id: z.uuid() });
const draftParams = z.object({ draftId: z.uuid() });
export const listDirectory = endpoint(
  "directory.list",
  "GET",
  "/api/agents/v1alpha1/directory",
  "List all three kinds of agent.",
)
  .input(z.object({}))
  .output(z.object({ items: z.array(AgentDetails) }));
export const get = endpoint(
  "get",
  "GET",
  "/api/agents/v1alpha1/{id}",
  "Read an agent and its versions.",
)
  .input(z.object({ params }))
  .output(AgentDetail);
export const create = endpoint("create", "POST", "/api/agents/v1alpha1", "Create an agent.")
  .input(
    z.object({
      body: z.strictObject({
        slug: CatalogSlug.optional().meta({
          description: "How tools such as the Lab name the agent. Generated when omitted.",
        }),
        role: AgentRole,
      }),
    }),
  )
  .output(AgentDetails);
export const createDraft = endpoint(
  "drafts.create",
  "POST",
  "/api/agents/v1alpha1/{id}/drafts",
  "Begin a draft of an agent.",
)
  .input(
    z.object({
      params,
      body: z.strictObject({
        from: z.uuid().nullable().meta({
          description:
            "The published version whose details and files the draft copies, or null to begin from nothing.",
        }),
      }),
    }),
  )
  .output(AgentVersionDetails);
export const updateDraft = endpoint(
  "drafts.update",
  "PATCH",
  "/api/agents/v1alpha1/drafts/{draftId}",
  "Change a draft's name, description, and notes.",
)
  .input(
    z.object({
      params: draftParams,
      body: z.strictObject({
        name: z.string().min(1).max(200),
        description: z.string().max(10000),
        about: z.string().max(50000),
      }),
    }),
  )
  .output(AgentVersionDetails);
export const pushDraftFile = endpoint(
  "drafts.files.set",
  "PUT",
  "/api/agents/v1alpha1/drafts/{draftId}/files/{role}",
  "Push one file to a draft.",
)
  .input(
    z.object({
      params: draftParams.extend({ role: AgentFileRole }),
      headers: z.object({ "x-content-sha256": Sha256 }),
      body: z.instanceof(ReadableStream),
    }),
  )
  .output(AgentVersionDetails);
export const publishDraft = endpoint(
  "drafts.publish",
  "POST",
  "/api/agents/v1alpha1/drafts/{draftId}/publish",
  "Check a draft's files and publish it as the agent's next version.",
)
  .input(z.object({ params: draftParams }))
  .output(AgentVersionDetails);
const authorParams = params.extend({ userId: z.uuid() });
export const setAuthor = endpoint(
  "authors.set",
  "PUT",
  "/api/agents/v1alpha1/{id}/authors/{userId}",
  "Make someone an agent author. They must hold agents.manage.",
)
  .input(z.object({ params: authorParams }))
  .output(z.object({ success: z.boolean() }));
export const removeAuthor = endpoint(
  "authors.delete",
  "DELETE",
  "/api/agents/v1alpha1/{id}/authors/{userId}",
  "Stop someone being an agent author. The owner always remains one.",
)
  .input(z.object({ params: authorParams }))
  .output(z.object({ success: z.boolean() }));
export const archive = endpoint(
  "archive.set",
  "PUT",
  "/api/agents/v1alpha1/{id}/archive",
  "Archive an agent, or restore it.",
)
  .input(z.object({ params, body: z.strictObject({ archived: z.boolean() }) }))
  .output(AgentDetails);

export const getInsights = endpoint(
  "insights.get",
  "GET",
  "/api/agents/v1alpha1/{id}/insights",
  "Read measured usage and player feedback.",
)
  .input(z.object({ params }))
  .output(
    z.object({
      calls: z.array(
        z.object({
          versionId: z.uuid(),
          gameId: z.uuid(),
          model: z.string(),
          date: z.string(),
          round: z.number().nullable(),
          inputTokens: z.number(),
          outputTokens: z.number(),
          cachedTokens: z.number(),
          costUsd: z.number().nullable(),
          toolCalls: z.number().nullable(),
        }),
      ),
      feedback: z.array(
        z.object({
          gameId: z.uuid(),
          gameTitle: z.string(),
          versionId: z.uuid(),
          author: z.string(),
          liked: z.boolean(),
          note: z.string(),
          date: z.string(),
        }),
      ),
    }),
  );

export const setFeedback = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "PUT",
      path: "/api/agents/v1alpha1/feedback/{gameId}",
      operationId: "agents.feedback.set",
      summary: "Rate the Game Master's writing in your game.",
      inputStructure: "detailed",
    }),
  )
  .errors({ ...errors.unauthorized, ...errors.forbidden, ...errors.notFound, ...errors.badRequest })
  .input(
    z.object({
      params: z.object({ gameId: z.uuid() }),
      body: z.object({ versionId: z.uuid(), liked: z.boolean(), note: z.string().max(5000) }),
    }),
  )
  .output(z.object({ success: z.boolean() }));
